/**
 * Account modal — real sign-up / sign-in / password reset / settings, backed by our own OAuth2
 * backend. This is the user's first branded touch-point, so it's designed to feel premium:
 * a gradient hero, icon-led inputs, gradient CTAs and a trust footer.
 */
import React, { useEffect, useState } from 'react';
import {
  X, LogIn, ShieldCheck, LogOut, UserPlus, CheckCircle2, AlertCircle, Info,
  Mail, Lock, ArrowRight, UserRound, Building2, DownloadCloud,
} from 'lucide-react';
import {
  signIn,
  completeTwoFactorChallenge,
  getTwoFactorStatus,
  disableTwoFactor,
  signUp,
  signOut,
  getSessionUser,
  sendPasswordReset,
  updatePassword,
  verifySignupCode,
  resendSignupCode,
} from '../../services/authClient.ts';
import { loadLatestClientProfile, setStoredProfileId, exportMyData, requestAccountDeletion } from '../../services/profileApi.ts';
import type { LoadedClientProfile } from '../../services/profileApi.ts';
import type { SessionUser, TwoFactorStatus } from '../../services/authClient.ts';
import TwoFactorSetup from './TwoFactorSetup.tsx';
import { twoFactorWording } from './twoFactorWording.ts';
import { acceptTerms } from '../../services/termsApi.ts';
import { CONSUMER_TERMS, PRIVACY_POLICY } from '../../constants/legal/index.ts';
import LegalDocModal, { type LegalDoc } from '../legal/LegalDocModal.tsx';
import CollectionNotice from '../legal/CollectionNotice.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { confirmAction } from '../ui/notices.tsx';

// ── Design tokens (shared, premium look) ────────────────────────────────────────────────────────
const label = 'block text-sm font-medium text-gray-700 mb-1.5';
const inputBase =
  'w-full rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-2.5 text-gray-900 placeholder-gray-400 shadow-sm transition focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/40 focus:outline-none';
const inputWithIcon = `${inputBase} pl-10`;
const primaryBtn =
  'w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] py-2.5 font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:shadow-xl hover:brightness-[1.04] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none';
const secondaryBtn =
  'w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-blue-600/50 bg-white py-2.5 font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-50';
const otpInput =
  'w-full rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-3 text-center text-2xl font-mono tracking-[0.6em] text-gray-900 shadow-inner transition focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-500/40 focus:outline-none';

type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

/** Input with a leading icon for a more finished feel. */
const IconField = ({ icon: Icon, ...props }: { icon: IconComponent } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <div className="relative">
    <Icon size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
    <input {...props} className={inputWithIcon} />
  </div>
);

type AccountModalMode = 'loading' | 'signin' | 'signup' | 'verify' | 'account' | 'forgot' | 'reset' | 'twofactor' | 'setup2fa';

interface Notice {
  tone: 'info' | 'error' | 'success';
  text: string;
}

interface AccountModalProps {
  onClose: () => void;
  setFactFind: (factFind: DraftFactFind) => void;
  initialMode?: string | null;
  resetToken?: string | null;
  context?: 'partner' | string;
  onSignedUp?: () => void | Promise<void>;
  onSignedIn?: (loaded: LoadedClientProfile | null) => void | Promise<void>;
  onSignOut?: () => void;
  /** Accepted but unused here — some call sites pass the live factFind alongside setFactFind. */
  factFind?: unknown;
}

const AccountModal = ({ onClose, setFactFind, initialMode, resetToken, context, onSignedUp, onSignedIn, onSignOut }: AccountModalProps) => {
  // loading | signin | signup | verify | account | forgot | reset
  const [mode, setMode] = useState<AccountModalMode>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  /** Held only between the password step and the code step — never persisted, never a session. */
  const [mfaToken, setMfaToken] = useState('');
  const [trustThisDevice, setTrustThisDevice] = useState(true);
  const [twoFactor, setTwoFactor] = useState<TwoFactorStatus | null>(null);
  /*
   * How we arrived at the setup screen, which decides where "Done" goes. Someone who came from their
   * account settings expects to land back there; an adviser who was just made to do this on the way
   * in expects to be at work. Sending the second one to a settings panel strands them one click short
   * of the thing they signed in to do.
   */
  const [setupCameFromSignIn, setSetupCameFromSignIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [agreedTerms, setAgreedTerms] = useState(false); // signup clickwrap checkbox
  const [viewDoc, setViewDoc] = useState<LegalDoc | null>(null); // legal document open in the reader

  useEffect(() => {
    (async () => {
      if (initialMode === 'reset') {
        setMode('reset');
        return;
      }
      const current = await getSessionUser();
      if (current) {
        if (onSignedIn) {
          setUser(current);
          const loaded = await loadLatestClientProfile().catch(() => null);
          if (loaded) setFactFind(loaded.factFind);
          onSignedIn(loaded);
          return;
        }
        setUser(current);
        setMode('account');
      } else {
        setMode(initialMode === 'signup' ? 'signup' : 'signin');
      }
    })();
  }, [initialMode]);

  const friendlyError = (raw: unknown): string => {
    const msg = typeof raw === 'string' ? raw : '';
    if (!msg || msg === '{}' || /unexpected_failure|error sending|fetch failed/i.test(msg)) {
      return "We couldn't send the email just now. Please try again in a few minutes — if it keeps happening, contact support.";
    }
    return msg;
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
    } catch (err: unknown) {
      const apiMsg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      const message = err instanceof Error ? err.message : undefined;
      setNotice({ tone: 'error', text: friendlyError(apiMsg || message) });
    } finally {
      setBusy(false);
    }
  };

  /** Keeps the security panel honest — it reads what the server says, never what we assume. */
  const refreshTwoFactor = async () => {
    setTwoFactor(await getTwoFactorStatus());
  };

  /*
   * Fetch it when the panel opens rather than at sign-in: it changes (they enrol, they turn it off,
   * they spend a recovery code), and a status read once and cached is how a screen ends up telling
   * someone the opposite of what is true.
   */
  useEffect(() => {
    if (mode !== 'account') return;
    let cancelled = false;
    (async () => {
      const status = await getTwoFactorStatus();
      if (!cancelled) setTwoFactor(status);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const finishSignIn = async () => {
    setUser(await getSessionUser());
    if (onSignedIn) {
      const loaded = await loadLatestClientProfile().catch(() => null);
      if (loaded) setFactFind(loaded.factFind);
      onSignedIn(loaded);
      return;
    }
    setMode('account');
  };

  const handleSignIn = () =>
    run(async () => {
      const res = await signIn(email.trim(), password);
      if (!res.ok) {
        /*
         * The password was right, and that is now only half of it. This must be handled BEFORE the
         * generic failure below: falling through would tell someone their password was wrong at the
         * exact moment it was accepted, which is the most confusing thing the screen could say.
         */
        if ('mfaRequired' in res && res.mfaRequired) {
          setMfaToken(res.mfaToken);
          setCode('');
          setTrustThisDevice(true);
          setMode('twofactor');
          return setNotice(null);
        }
        // They created an account but never entered the emailed code: don't dead-end on a raw
        // error — send a fresh code and take them straight to the confirmation screen.
        if ('needsEmailConfirm' in res && res.needsEmailConfirm) {
          await resendSignupCode(email.trim()).catch(() => {});
          setCode('');
          setMode('verify');
          return setNotice({
            tone: 'info',
            text: `Your email hasn't been confirmed yet. We've just sent a fresh code to ${email.trim()} — enter it below to finish.`,
          });
        }
        return setNotice({ tone: 'error', text: 'message' in res ? res.message : 'Incorrect email or password.' });
      }

      /*
       * An adviser or firm member without an authenticator yet. They ARE signed in — they have to be,
       * or they could never reach this screen — but almost everything is refused until they finish,
       * so dropping them on a dashboard that says no to everything would just look broken.
       */
      if (res.mustSetUpTwoFactor) {
        await refreshTwoFactor();
        setSetupCameFromSignIn(true);
        setMode('setup2fa');
        return setNotice({
          tone: 'info',
          text: 'Adviser accounts need a second step at sign in. It takes about a minute to set up.',
        });
      }

      await finishSignIn();
    });

  /**
   * The second step of signing in. The same box takes a six-digit code or a recovery code, because
   * someone reaching for a recovery code has already lost their phone and should not also have to
   * find a different screen.
   */
  const handleTwoFactor = () =>
    run(async () => {
      const res = await completeTwoFactorChallenge(mfaToken, code.trim(), trustThisDevice);
      if (!res.ok) {
        setCode('');
        return setNotice({ tone: 'error', text: res.message });
      }
      if (res.usedRecoveryCode) {
        // Say this plainly: a code they can never use again has just been spent.
        setNotice({
          tone: 'info',
          text:
            res.recoveryCodesRemaining === 0
              ? 'That recovery code is now used, and it was your last one. Set up new ones from Account as soon as you can.'
              : `That recovery code is now used — you have ${res.recoveryCodesRemaining} left.`,
        });
      }
      await finishSignIn();
    });

  const handleSignUp = () =>
    run(async () => {
      if (password.length < 10) {
        return setNotice({ tone: 'error', text: 'Please use a password of at least 10 characters.' });
      }
      if (!agreedTerms) {
        return setNotice({ tone: 'error', text: 'Please tick the box to agree to the Terms of Use and Privacy Policy first.' });
      }
      const res = await signUp(email.trim(), password);
      if (!res.ok) return setNotice({ tone: 'error', text: friendlyError(res.message) });
      if (res.needsEmailConfirm) {
        // Single-screen confirmation: stay right here and ask for the 6-digit code we email.
        // No confirmation link, so no second browser tab to get lost in.
        setCode('');
        setMode('verify');
        return setNotice({
          tone: 'info',
          text: `We've emailed a code to ${email.trim()}. Enter it below to finish.`,
        });
      }
      // A live session exists already: straight into the app.
      await continueIntoApp();
    });

  /**
   * A fresh session is live (just signed up, or just confirmed a code): route the person INTO the
   * app. onSignedUp/onSignedIn navigate to the fact-find or their saved plan; the settings panel
   * is only ever reached deliberately from inside the app.
   */
  const continueIntoApp = async () => {
    setUser(await getSessionUser());
    if (agreedTerms) {
      // Record the clickwrap acceptance on the server ledger now a session exists. Fire-and-forget:
      // a transient failure must not block entry (the server's status endpoint lets us re-prompt).
      acceptTerms(['consumer_terms', 'privacy_policy'], 'signup').catch(() => {});
    }
    if (onSignedUp) {
      onSignedUp();
      return;
    }
    await finishSignIn();
  };

  const handleVerifyCode = () =>
    run(async () => {
      const res = await verifySignupCode(email.trim(), code);
      if (!res.ok) {
        return setNotice({ tone: 'error', text: "That code wasn't accepted — check the digits in your email and try again." });
      }
      setCode('');
      setNotice(null);
      // A new account that must use an authenticator (a platform admin): set it up now, as a
      // password sign-in would, rather than landing on screens that refuse a password-only session.
      if (res.mustSetUpTwoFactor) {
        await refreshTwoFactor();
        setSetupCameFromSignIn(true);
        setMode('setup2fa');
        return setNotice({ tone: 'info', text: 'This account needs a second step at sign in. It takes about a minute to set up.' });
      }
      await continueIntoApp();
    });

  const handleResendCode = () =>
    run(async () => {
      const res = await resendSignupCode(email.trim());
      if (!res.ok) return setNotice({ tone: 'error', text: friendlyError(res.message) });
      setNotice({ tone: 'success', text: `A fresh code is on its way to ${email.trim()}.` });
    });

  const handleForgot = () =>
    run(async () => {
      const res = await sendPasswordReset(email.trim());
      if (!res.ok) return setNotice({ tone: 'error', text: friendlyError(res.message) });
      if (!res.exists) {
        return setNotice({
          tone: 'error',
          text: "We couldn't find an account with that email address. Check the spelling, or create a new account instead.",
        });
      }
      setNotice({
        tone: 'success',
        text: `Your reset link is on its way to ${email.trim()}. It usually arrives within a minute — check your junk folder if you don't see it.`,
      });
    });

  const handleSetNewPassword = () =>
    run(async () => {
      if (password.length < 10) return setNotice({ tone: 'error', text: 'Please use a password of at least 10 characters.' });
      if (password !== password2) return setNotice({ tone: 'error', text: 'The two passwords do not match.' });
      const res = await updatePassword(resetToken || '', password);
      if (!res.ok) return setNotice({ tone: 'error', text: friendlyError(res.message) });
      setPassword('');
      setPassword2('');
      // Password changed and the user is signed in — route them into their proper workspace
      // (partner portal / their plan), NOT the settings panel. Only fall back to the settings
      // view when this modal has no app-navigation handler (i.e. opened from inside the app).
      if (onSignedIn || onSignedUp) {
        await continueIntoApp();
        return;
      }
      setUser(await getSessionUser());
      setMode('account');
      setNotice({ tone: 'success', text: 'Your password has been changed. You are signed in.' });
    });

  const handleSignOut = () =>
    run(async () => {
      await signOut();
      setStoredProfileId(null);
      setUser(null);
      setPassword('');
      setMode('signin');
      if (onSignOut) onSignOut();
    });

  const noticeStyle =
    notice &&
    {
      error: { Icon: AlertCircle, chip: 'bg-red-100 text-red-600', ring: 'border-red-200' },
      success: { Icon: CheckCircle2, chip: 'bg-emerald-100 text-emerald-600', ring: 'border-emerald-200' },
      info: { Icon: Info, chip: 'bg-blue-100 text-blue-600', ring: 'border-blue-200' },
    }[notice.tone];

  // Hero title + supportive subtitle, tuned per mode (and per channel for sign-in).
  const isPartner = context === 'partner';
  /* Advisers and firm staff see the professional term; consumers see plain words. */
  const twoFactorWords = twoFactorWording(Boolean(twoFactor?.required));

  const HeroIcon = mode === 'twofactor' || mode === 'setup2fa' ? ShieldCheck
    : mode === 'account' ? UserRound
    : mode === 'verify' ? Mail
    : mode === 'reset' || mode === 'forgot' ? ShieldCheck
    : isPartner ? Building2 : LogIn;
  const heading = {
    loading: 'One moment…',
    signin: isPartner ? 'Partner sign in' : 'Welcome back',
    signup: 'Create your account',
    verify: 'Confirm your email',
    account: 'Your account',
    forgot: 'Reset your password',
    reset: 'Choose a new password',
    twofactor: 'One more step',
    setup2fa: twoFactorWords.title,
  }[mode];
  const subtitle = {
    loading: 'Checking your session',
    signin: isPartner ? 'Sign in to your firm’s secure workspace' : 'Sign in to pick up right where you left off',
    signup: 'It takes about a minute — your details are encrypted',
    verify: 'Enter the code we just emailed you',
    account: user?.email || 'Manage your security and privacy',
    forgot: 'We’ll email you a secure link to reset it',
    reset: 'Almost done — set a new password below',
    twofactor: 'Enter the code from your authenticator app',
    setup2fa: 'Add a code from your phone to your login',
  }[mode];

  const showTrustFooter = mode === 'signin' || mode === 'signup' || mode === 'verify' || mode === 'forgot' || mode === 'twofactor';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="no-scrollbar w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 max-h-[92vh] overflow-y-auto">
        {/* Gradient hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-6 pt-6 pb-7 text-white">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          {/*
            An adviser part-way through setup they cannot decline gets SIGN OUT here instead of a
            close button. Closing would drop them into a portal that refuses every request — signed
            in, and unable to do anything, with no explanation on screen. They still have a way out;
            it is just an honest one.
          */}
          {mode === 'setup2fa' && setupCameFromSignIn ? (
            <button
              type="button"
              onClick={handleSignOut}
              disabled={busy}
              className="absolute right-4 top-4 rounded-full px-3 py-1.5 text-xs font-medium text-white/85 transition hover:bg-white/15 hover:text-white"
            >
              Sign out
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full p-1.5 text-white/80 transition hover:bg-white/15 hover:text-white"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
          <div className="flex items-center gap-3.5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
              <HeroIcon className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight">{heading}</h2>
              <p className="text-sm text-white/85">{subtitle}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {notice && (
            <div className={`flex items-start gap-3 rounded-xl border bg-white px-4 py-3 shadow-sm ${noticeStyle!.ring}`}>
              <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${noticeStyle!.chip}`}>
                {noticeStyle && <noticeStyle.Icon size={16} aria-hidden="true" />}
              </span>
              <p className="text-sm leading-relaxed text-gray-700">{notice.text}</p>
            </div>
          )}

          {mode === 'loading' && (
            <div className="flex items-center justify-center gap-3 py-6 text-sm text-gray-500">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-blue-100 border-t-blue-500" />
              Checking your session…
            </div>
          )}

          {(mode === 'signin' || mode === 'signup') && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                (mode === 'signin' ? handleSignIn : handleSignUp)();
              }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="account-email" className={label}>Email</label>
                <IconField icon={Mail} id="account-email" type="email" required autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div>
                <label htmlFor="account-password" className={label}>Password</label>
                <IconField icon={Lock} id="account-password" type="password" required
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'At least 10 characters' : '••••••••••'} />
              </div>
              {mode === 'signup' && (
                <>
                <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-gray-200 bg-gray-50/70 px-3.5 py-3">
                  <input
                    type="checkbox"
                    checked={agreedTerms}
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs leading-relaxed text-gray-600">
                    I have read and agree to the{' '}
                    <button type="button" onClick={() => setViewDoc(CONSUMER_TERMS)}
                      className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700">
                      Terms of Use
                    </button>{' '}
                    and the{' '}
                    <button type="button" onClick={() => setViewDoc(PRIVACY_POLICY)}
                      className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700">
                      Privacy Policy
                    </button>
                    .
                  </span>
                </label>
                {/* APP 5: the collection notice at the point of collection, not only in the policy. */}
                <CollectionNotice variant="signup" />
                </>
              )}
              <button type="submit" disabled={busy || (mode === 'signup' && !agreedTerms)} className={primaryBtn}>
                {busy ? 'Working…' : (<>{mode === 'signin' ? 'Sign in' : 'Create account'} <ArrowRight size={16} /></>)}
              </button>
              {mode === 'signin' && (
                <button type="button" disabled={busy}
                  onClick={() => { setNotice(null); setMode('forgot'); }}
                  className="w-full text-sm text-gray-500 transition hover:text-gray-700">
                  Forgot your password?
                </button>
              )}
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-100" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-2 text-xs text-gray-400">or</span></div>
              </div>
              <button type="button" disabled={busy}
                onClick={() => { setNotice(null); setMode(mode === 'signin' ? 'signup' : 'signin'); }}
                className="flex w-full items-center justify-center gap-1.5 text-sm font-medium text-blue-600 transition hover:text-blue-700">
                {mode === 'signin' ? (<><UserPlus size={15} /> New here? Create an account</>) : (<><LogIn size={15} /> Already have an account? Sign in</>)}
              </button>
            </form>
          )}

          {mode === 'verify' && (
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} className="space-y-4">
              {/*
                This used to reassure people that they would "stay right here — there's no link to
                click and no other page to open". That was written against an older sign-up that threw
                you between screens; on this one-screen version it answers a worry nobody has, and it
                spent the only line of copy on the page saying nothing useful.
                The real question at this moment is "where is my email?", so the copy answers that.
              */}
              <p className="text-sm text-gray-600">
                Enter the code we emailed to <strong>{email}</strong>. It can take a minute to
                arrive — please check your junk or spam folder before asking for a new one.
              </p>
              <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} autoFocus
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className={`${otpInput} tracking-[0.35em]`} placeholder="Enter code" />
              <button type="submit" disabled={busy || code.length < 6} className={primaryBtn}>
                {busy ? 'Checking…' : (<>Confirm &amp; continue <ArrowRight size={16} /></>)}
              </button>
              <button type="button" disabled={busy} onClick={handleResendCode}
                className="w-full text-sm text-gray-500 transition hover:text-gray-700">
                Didn&apos;t get it? Resend the code
              </button>
            </form>
          )}

          {mode === 'twofactor' && (
            <form onSubmit={(e) => { e.preventDefault(); handleTwoFactor(); }} className="space-y-4">
              <p className="text-sm text-gray-600">
                Open your authenticator app and enter the 6-digit code it shows for AgedCareWise.
              </p>
              <input
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                maxLength={20}
                value={code}
                autoFocus
                onChange={(e) => setCode(e.target.value)}
                className={`${otpInput} tracking-[0.35em]`}
                placeholder="000000"
                aria-label="Authentication code"
              />

              {/*
                Ticked by default, and the single biggest reason people tolerate two-factor at all:
                without it, someone signing in from their own desk every morning reaches for their
                phone every morning.
              */}
              <label className="flex items-start gap-2.5 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={trustThisDevice}
                  onChange={(e) => setTrustThisDevice(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span>
                  Don&apos;t ask again on this device for 30 days
                  <span className="mt-0.5 block text-xs text-gray-400">
                    Only tick this on a computer that&apos;s yours.
                  </span>
                </span>
              </label>

              <button type="submit" disabled={busy || code.trim().length < 6} className={primaryBtn}>
                {busy ? 'Checking…' : (<>Confirm &amp; sign in <ArrowRight size={16} /></>)}
              </button>

              <p className="text-center text-xs text-gray-500">
                Lost your phone? Enter one of your recovery codes in the box above instead.
              </p>

              <button
                type="button"
                disabled={busy}
                onClick={() => { setNotice(null); setCode(''); setMfaToken(''); setMode('signin'); }}
                className="w-full text-sm text-blue-600 transition hover:text-blue-700"
              >
                Back to sign in
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={(e) => { e.preventDefault(); handleForgot(); }} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className={label}>Email</label>
                <IconField icon={Mail} id="forgot-email" type="email" required autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <button type="submit" disabled={busy} className={primaryBtn}>
                {busy ? 'Sending…' : (<>Email me a reset link <ArrowRight size={16} /></>)}
              </button>
              <button type="button" disabled={busy} onClick={() => { setNotice(null); setMode('signin'); }}
                className="w-full text-sm text-blue-600 transition hover:text-blue-700">
                Back to sign in
              </button>
            </form>
          )}

          {mode === 'reset' && (
            <form onSubmit={(e) => { e.preventDefault(); handleSetNewPassword(); }} className="space-y-4">
              <div>
                <label htmlFor="reset-password" className={label}>New password</label>
                <IconField icon={Lock} id="reset-password" type="password" required autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 10 characters" />
              </div>
              <div>
                <label htmlFor="reset-password2" className={label}>Repeat new password</label>
                <IconField icon={Lock} id="reset-password2" type="password" required autoComplete="new-password"
                  value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="Same password again" />
              </div>
              <button type="submit" disabled={busy} className={primaryBtn}>{busy ? 'Saving…' : 'Set new password'}</button>
            </form>
          )}

          {mode === 'setup2fa' && (
            <TwoFactorSetup
              mandatory={Boolean(twoFactor?.required)}
              onDone={async () => {
                await refreshTwoFactor();
                if (setupCameFromSignIn) {
                  // Straight on into the app — this was the last thing between them and their work.
                  setSetupCameFromSignIn(false);
                  await finishSignIn();
                  return;
                }
                setMode('account');
                setNotice({ tone: 'success', text: `${twoFactorWords.isOn}. You have been signed out on your other devices.` });
              }}
              onCancel={() => { setNotice(null); setSetupCameFromSignIn(false); setMode('account'); }}
            />
          )}

          {mode === 'account' && (
            <div className="space-y-5">
              {/*
                Sign-in security. Rendered from the server's answer rather than from anything held
                here, so it cannot drift out of step with what is actually true of the account.
              */}
              <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-800">Sign-in security</h3>

                {twoFactor === null ? (
                  <div className="h-9 animate-pulse rounded-lg bg-gray-100" />
                ) : twoFactor.enrolled ? (
                  <>
                    <p className="flex items-center gap-1.5 text-sm text-green-700">
                      <ShieldCheck size={15} /> {twoFactorWords.isOn}
                    </p>
                    <p className="text-xs text-gray-500">
                      {twoFactor.recoveryCodesRemaining > 0
                        ? `${twoFactor.recoveryCodesRemaining} recovery ${twoFactor.recoveryCodesRemaining === 1 ? 'code' : 'codes'} left.`
                        : 'You have no recovery codes left — if you lose your phone you will need us to reset it.'}
                    </p>
                    {twoFactor.required ? (
                      <p className="text-xs text-gray-500">
                        Adviser and firm accounts must keep this on.
                      </p>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        className="w-full pt-1 text-sm text-gray-500 transition hover:text-red-600"
                        onClick={() => run(async () => {
                          if (!(await confirmAction(`${twoFactorWords.turnOff}? A password alone will then be enough to get into your account.`))) return;
                          const res = await disableTwoFactor();
                          if (!res.ok) return setNotice({ tone: 'error', text: res.message });
                          await refreshTwoFactor();
                          setNotice({ tone: 'info', text: `${twoFactorWords.isOff}. Any other devices you were signed in on have been signed out.` });
                        })}
                      >
                        {twoFactorWords.turnOff}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      {twoFactor.required
                        ? 'Your account needs a code from your phone as well as a password.'
                        : 'Add a code from your phone to your login, so a password alone is never enough.'}
                    </p>
                    <button type="button" disabled={busy} className={secondaryBtn}
                      onClick={() => { setNotice(null); setSetupCameFromSignIn(false); setMode('setup2fa'); }}>
                      <ShieldCheck size={16} /> {twoFactorWords.setUp}
                    </button>
                  </>
                )}
              </div>

              <div className="rounded-xl border border-gray-200 p-4 space-y-2">
                <h3 className="text-sm font-semibold text-gray-800">Your privacy rights</h3>
                <button type="button" disabled={busy} className={secondaryBtn}
                  onClick={() => run(async () => {
                    const data = await exportMyData();
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'agedcarewise-my-data.json';
                    a.click();
                    URL.revokeObjectURL(url);
                    setNotice({ tone: 'success', text: 'Your data has been downloaded as a file on this computer.' });
                  })}>
                  <DownloadCloud size={16} /> Download my data
                </button>
                <button type="button" disabled={busy} className="w-full pt-1 text-sm text-gray-500 transition hover:text-red-600"
                  onClick={() => run(async () => {
                    if (!(await confirmAction('Request deletion of your account and data? This will be actioned within 30 days.'))) return;
                    const res = await requestAccountDeletion();
                    setNotice({ tone: 'info', text: typeof res.message === 'string' ? res.message : 'Your deletion request has been received.' });
                  })}>
                  Request account deletion
                </button>
              </div>

              <button type="button" onClick={onClose} disabled={busy} className={primaryBtn}>
                {isPartner ? 'Done' : 'Done — back to my plan'} <ArrowRight size={16} />
              </button>

              <button type="button" onClick={handleSignOut} disabled={busy}
                className="flex w-full items-center justify-center gap-1.5 text-sm text-gray-500 transition hover:text-gray-700">
                <LogOut size={14} /> Sign out
              </button>
            </div>
          )}

          {showTrustFooter && (
            <div className="flex items-center justify-center gap-1.5 pt-1 text-xs text-gray-400">
              <Lock size={12} /> Bank-level encryption · Your data is stored in Australia
            </div>
          )}
        </div>
      </div>
      {viewDoc && <LegalDocModal doc={viewDoc} onClose={() => setViewDoc(null)} />}
    </div>
  );
};

export default AccountModal;
